import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Search, Send, Zap, CheckCircle, Clock, Loader, Paperclip, Mic, ArrowLeft, ChevronDown, ChevronUp, Target, BarChart3, ArrowUpRight, Shield, Bot, Check, ClipboardList } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { SALES_DEPT_DATA, SALES_DEPT_CHAT_RESPONSES, SALES_DEEP_DIVE, SALES_DECISION_DEEP_DIVES, SALES_ALERT_DEEP_DIVES, SALES_DEAL_DEEP_DIVES } from '../../data/salesDeptData';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { scaleDelay } from '../../hooks/demoSpeed';

const AGENT_NAMES = { stark: 'Stark', luc: 'Luc', sally: 'Sally', david: 'David' };
const STATUS_COLOR = {
  green: { ring: '#34d399', text: 'text-emerald-400', lightText: 'text-emerald-600' },
  amber: { ring: '#fbbf24', text: 'text-amber-400', lightText: 'text-amber-600' },
  red: { ring: '#f87171', text: 'text-red-400', lightText: 'text-red-500' },
};
const ACTION_STATUS = {
  'complete': { icon: CheckCircle, dark: 'text-emerald-400', light: 'text-emerald-600' },
  'in-progress': { icon: Loader, dark: 'text-blue-400', light: 'text-blue-600' },
  'pending-approval': { icon: Clock, dark: 'text-amber-400', light: 'text-amber-600' },
  'recommended': { icon: Zap, dark: 'text-indigo-400', light: 'text-indigo-600' },
};

/* ── Score Ring ── */
const ScoreRing = ({ score, size, strokeWidth, isDark, status = 'green' }) => {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const p = (score / 100) * c;
  const s = STATUS_COLOR[status];
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.ring} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - p} className="transition-all duration-700" />
      </svg>
      <span className={`absolute text-lg font-bold ${isDark ? s.text : s.lightText}`}>{score}</span>
    </div>
  );
};

/* ── Agent Card (matches Marketing pattern) ── */
const AgentCard = ({ agent, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const thumb = AGENT_THUMBS[agent.id];
  return (
    <div className={`px-3 py-3 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`} data-testid={`sales-agent-${agent.id}`}>
      <div className="flex items-center gap-2.5 mb-2">
        {thumb && <img src={thumb} alt="" className="w-8 h-8 rounded-full object-cover" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-xs font-semibold ${tp}`}>{agent.name}</p>
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${agent.status === 'active' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'}`} />
          </div>
          <p className={`text-[10px] ${ts}`}>{agent.role}</p>
        </div>
      </div>
      <p className={`text-[11px] leading-relaxed mb-1.5 ${tp}`}>{agent.currentTask}</p>
      <p className={`text-[10px] ${ts}`}>Last: {agent.lastAction} · {agent.lastActionTime}</p>
    </div>
  );
};

/* ── Alert Card (matches Marketing pattern) ── */
const AlertCard = ({ alert, isDark, onDeepDive, onEscalate, escalated, addressed }) => {
  const [expanded, setExpanded] = useState(false);
  const isHigh = alert.severity === 'high';
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = addressed ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50') : (isHigh ? (isDark ? 'bg-red-950/20' : 'bg-red-50/60') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'));
  const cardBorder = addressed ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/60') : (isHigh ? (isDark ? 'border-red-500/15' : 'border-red-200/60') : border);
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const sevColor = isHigh ? (isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-600/10') : (isDark ? 'text-amber-400 bg-amber-400/10' : 'text-amber-700 bg-amber-700/10');

  return (
    <div className={`${cardBg} border ${cardBorder} rounded-lg px-3 py-3`} data-testid={`sales-alert-${alert.id}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${addressed ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isHigh ? (isDark ? 'text-red-400' : 'text-red-500') : (isDark ? 'text-amber-400' : 'text-amber-600'))}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-xs font-semibold flex-1 ${tp}`}>{alert.title}</p>
            {addressed ? (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}><CheckCircle className="w-2.5 h-2.5" /> Actioned · David owns</span>
            ) : escalated ? (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? 'text-indigo-400 bg-indigo-400/10' : 'text-indigo-600 bg-indigo-600/10'}`}><ArrowUpRight className="w-2.5 h-2.5" /> Escalated</span>
            ) : (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${sevColor}`}>{isHigh ? 'High' : 'Medium'}</span>
            )}
          </div>
          <p className={`text-[10px] mt-1.5 leading-relaxed ${ts}`}>{alert.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-2.5 ml-5">
        <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${ts} ${hoverBg}`}>
          {expanded ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
          Details
        </button>
        {!addressed && (
          <button onClick={() => onDeepDive(alert)} className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`} data-testid={`sales-alert-dive-${alert.id}`}>
            <Search className="w-2.5 h-2.5" /> Deep Dive
          </button>
        )}
        {!escalated && !addressed && (
          <button onClick={() => onEscalate(alert.id)} className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20' : 'bg-rose-500/10 text-rose-600 hover:bg-rose-500/15'}`}>
            <ArrowUpRight className="w-2.5 h-2.5" /> Escalate to David
          </button>
        )}
      </div>
      {expanded && (
        <div className={`mt-2.5 ml-5 p-2.5 rounded-md space-y-1.5 ${isDark ? 'bg-white/[0.03]' : 'bg-black/[0.02]'}`}>
          {alert.actions.map((a, i) => {
            const st = ACTION_STATUS[a.status] || ACTION_STATUS['recommended'];
            const Icon = st.icon;
            return (
              <div key={i} className="flex items-center gap-2">
                <Icon className={`w-3 h-3 flex-shrink-0 ${isDark ? st.dark : st.light}`} />
                <span className={`text-[10px] ${tp}`}>{a.label}</span>
                <span className={`text-[9px] ${ts}`}>— {AGENT_NAMES[a.agent]}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Decision Card (matches Marketing) ── */
const DecisionCard = ({ item, isDark, onApprove, onDeepDive, approved }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = approved ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const thumb = AGENT_THUMBS[item.agent];

  return (
    <div className={`${cardBg} border ${approved ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/60') : border} rounded-lg px-3 py-3`} data-testid={`sales-decision-${item.id}`}>
      <div className="flex items-start gap-2">
        <Shield className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-xs font-semibold flex-1 ${tp}`}>{item.title}</p>
            {item.urgency === 'high' && !approved && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-600/10'}`}>Urgent</span>}
            {approved && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1 ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}><CheckCircle className="w-2.5 h-2.5" /> Approved</span>}
          </div>
          <p className={`text-[10px] mt-1.5 leading-relaxed ${ts}`}>{item.detail}</p>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">
              {thumb && <img src={thumb} alt="" className="w-4 h-4 rounded-full object-cover" />}
              <span className={`text-[9px] ${ts}`}>{AGENT_NAMES[item.agent]}</span>
            </div>
            {!approved && (
              <div className="flex items-center gap-1.5">
                <button onClick={() => onDeepDive && onDeepDive(item.id)} className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'} transition-colors`} data-testid={`sales-decision-deep-dive-${item.id}`}>
                  <Search className="w-2.5 h-2.5" /> Deep Dive
                </button>
                <button onClick={() => onApprove(item.id)} className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium ${isDark ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15'} transition-colors`} data-testid={`sales-approve-${item.id}`}>
                  <CheckCircle className="w-2.5 h-2.5" /> Approve
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Deal Row ── */
const DealRow = ({ deal, isDark, onDeepDive }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const atRisk = deal.health === 'at-risk';

  return (
    <div className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg border ${atRisk ? (isDark ? 'border-red-500/15 bg-red-950/10' : 'border-red-200/50 bg-red-50/30') : `${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}`} data-testid={`sales-deal-${deal.id}`}>
      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${atRisk ? 'bg-red-400' : 'bg-emerald-400'}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] font-semibold truncate ${tp}`}>{deal.name}</p>
        <p className={`text-[9px] mt-0.5 ${ts}`}>{deal.stage} · {deal.daysInStage}d · {deal.owner}</p>
      </div>
      <button
        onClick={() => onDeepDive && onDeepDive(deal.id)}
        className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`}
        data-testid={`sales-deal-deep-dive-${deal.id}`}
      >
        <Search className="w-2.5 h-2.5" /> Deep Dive
      </button>
      <span className={`text-xs font-bold flex-shrink-0 ${atRisk ? (isDark ? 'text-red-400' : 'text-red-500') : tp}`}>{deal.value}</span>
    </div>
  );
};

/* ── Pipeline Funnel ── */
const PipelineFunnel = ({ stages, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const maxVal = Math.max(...stages.map(s => s.count));
  const colors = ['bg-indigo-500/35', 'bg-blue-500/35', 'bg-cyan-500/35', 'bg-emerald-500/35', 'bg-purple-500/35'];

  return (
    <div className={`px-3 py-3 rounded-lg border ${isDark ? 'border-white/[0.06] bg-[#0d1b2a]' : 'border-black/[0.06] bg-[#f0ece4]'} space-y-2.5`} data-testid="sales-pipeline-funnel">
      {stages.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className={`text-[9px] w-[70px] text-right truncate ${ts}`}>{s.stage}</span>
          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
            <div className={`h-full rounded-full ${colors[i]} transition-all duration-500`} style={{ width: `${(s.count / maxVal) * 100}%` }} />
          </div>
          <span className={`text-[10px] font-bold w-6 text-right ${tp}`}>{s.count}</span>
          <span className={`text-[9px] w-10 text-right ${ts}`}>{s.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ── Activity Item (matches Marketing) ── */
const ActivityItem = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const thumb = AGENT_THUMBS[item.agent];
  const typeDot = item.type === 'alert' ? 'bg-red-400' : item.type === 'insight' ? 'bg-indigo-400' : 'bg-blue-400';

  return (
    <div className="flex items-start gap-2.5 py-2.5" data-testid={`sales-activity-${item.id}`}>
      {thumb && <img src={thumb} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>
          <span className="font-semibold">{AGENT_NAMES[item.agent]}</span> {item.action}
        </p>
        <p className={`text-[9px] mt-0.5 ${ts}`}>{item.time}</p>
      </div>
      <div className={`w-1.5 h-1.5 rounded-full ${typeDot} flex-shrink-0 mt-2`} />
    </div>
  );
};

/* ── Chat Message ── */
const ChatMessage = ({ msg, isDark, onChipClick, chipsDisabled }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="sales-chat-msg-user">
        <div className={`max-w-[85%] px-5 py-3.5 rounded-2xl rounded-br-md text-[14px] leading-[1.8] ${isDark ? 'bg-indigo-500/20 text-white' : 'bg-indigo-100 border border-indigo-200/80 text-slate-800'}`}>{msg.text}</div>
      </div>
    );
  }

  if (msg.role === 'thinking') {
    return (
      <div className="flex gap-4 items-center" data-testid="sales-chat-thinking">
        <img src={AGENT_THUMBS.sally} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
        <div className="flex items-center gap-2.5">
          <span className={`text-[13px] italic ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Sally is thinking</span>
          <span className="flex gap-1">
            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/35' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '0ms' }} />
            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/35' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '150ms' }} />
            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/35' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '300ms' }} />
          </span>
        </div>
      </div>
    );
  }

  if (msg.role === 'complete-pill') {
    return (
      <div className="flex justify-center" data-testid="sales-chat-complete">
        <div className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-medium ${isDark ? 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/15' : 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/15'}`}>
          <Check className="w-3 h-3" /> Conversation complete
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4" data-testid="sales-chat-msg-agent">
      <img src={AGENT_THUMBS.sally} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 space-y-4">
        {msg.text && <div className={`text-[14px] leading-[1.8] whitespace-pre-line ${tp}`}>{msg.text}</div>}
        {msg.chips && !chipsDisabled && (
          <div className="flex flex-wrap gap-2 pt-3" data-testid="sales-chips">
            {msg.chips.map((c) => (
              <button key={c.id} onClick={() => onChipClick && onChipClick(c)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-medium border transition-all ${isDark ? 'bg-white/[0.04] border-white/[0.08] text-white/80 hover:bg-indigo-500/15 hover:border-indigo-500/30 hover:text-white' : 'bg-white border-black/[0.08] text-slate-700 hover:bg-indigo-500/10 hover:border-indigo-500/30 hover:text-slate-900'}`}
                data-testid={`sales-chip-${c.id}`}>{c.label}</button>
            ))}
          </div>
        )}
        {msg.executionSteps && (
          <div className={`rounded-xl px-4 py-3 ${isDark ? 'bg-indigo-500/8 border border-indigo-500/10' : 'bg-indigo-50 border border-indigo-200/40'}`} data-testid="sales-exec-progress">
            <div className="space-y-2">
              {msg.executionSteps.map((step, i) => (
                <div key={i} className="flex items-center gap-2.5" style={{ animation: `salesFade 0.35s ease-out ${i * 0.25}s both` }}>
                  <Check className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <span className={`text-[13px] ${isDark ? 'text-white/75' : 'text-slate-700'}`}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {msg.davidTask && (
          <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/[0.02] border-white/[0.08]' : 'bg-white/80 border-black/[0.06]'}`} data-testid="sales-david-task">
            <div className={`px-4 py-2.5 border-b ${border} flex items-center gap-2 ${isDark ? 'bg-gradient-to-r from-purple-500/8 to-indigo-500/8' : 'bg-gradient-to-r from-purple-50 to-indigo-50'}`}>
              <ClipboardList className={`w-3.5 h-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Task routed to David</span>
            </div>
            <div className="px-4 py-3">
              <p className={`text-[13px] font-semibold leading-snug ${tp}`}>{msg.davidTask.title}</p>
              <p className={`text-[12px] leading-relaxed mt-1 ${ts}`}>{msg.davidTask.context}</p>
              <div className={`flex items-center gap-3 mt-2.5 pt-2.5 border-t ${border}`}>
                <div className="flex items-center gap-1.5">
                  <img src={AGENT_THUMBS[msg.davidTask.agent]} alt="" className="w-4 h-4 rounded-full object-cover" />
                  <span className={`text-[11px] ${ts}`}>{AGENT_NAMES[msg.davidTask.agent] || msg.davidTask.agent}</span>
                </div>
                <div className="flex items-center gap-1"><Clock className={`w-3 h-3 ${ts}`} /><span className={`text-[11px] ${ts}`}>{msg.davidTask.dueDate}</span></div>
                <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-400/10 text-amber-400' : 'bg-amber-500/10 text-amber-600'}`}>Pending with David</span>
              </div>
            </div>
          </div>
        )}
        {msg.closure && (
          <div className={`text-[13px] italic leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>{msg.closure}</div>
        )}
        {msg.actions && (
          <div className="flex flex-col gap-2">
            {msg.actions.map((a, i) => {
              const st = ACTION_STATUS[a.status] || ACTION_STATUS['recommended'];
              const Icon = st.icon;
              const thumb = AGENT_THUMBS[a.agent];
              return (
                <div key={i} className={`flex items-start gap-2.5 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]'}`}>
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? st.dark : st.light}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] leading-relaxed ${tp}`}>{a.label}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      {thumb && <img src={thumb} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />}
                      <span className={`text-[11px] ${ts}`}>{AGENT_NAMES[a.agent]}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {msg.timeline && (
          <div className={`flex items-start gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-indigo-500/5 border border-indigo-500/10' : 'bg-indigo-500/5 border border-indigo-500/10'}`}>
            <Clock className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-[12px] leading-relaxed ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>{msg.timeline}</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* Normalize a deep-dive turn into a chat message. */
const normalizeTurn = (turn) => ({
  role: 'agent',
  text: turn.text || null,
  chips: turn.chips || null,
  executionSteps: turn.executionSteps || null,
  davidTask: turn.davidTask || null,
  closure: turn.closure || null,
});

/* ════════════════════════════════════════ */
/* ── Main Component ── */
/* ════════════════════════════════════════ */

const V4SalesDept = ({ isDark, onWorkflowToggle, workflowPanelOpen, onToast }) => {
  const data = SALES_DEPT_DATA;
  const [approvedDecisions, setApprovedDecisions] = useState(new Set());
  const [escalatedAlerts, setEscalatedAlerts] = useState(new Set());
  const [addressedAlerts, setAddressedAlerts] = useState(new Set());
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [deepDiveBranch, setDeepDiveBranch] = useState(null);
  const [deepDiveTurn, setDeepDiveTurn] = useState(0);
  const messagesEndRef = useRef(null);
  const deepDiveTimersRef = useRef([]);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    const userMsg = { role: 'user', text };
    const agentMsg = { role: 'agent', text: resp?.text || SALES_DEPT_CHAT_RESPONSES.general.text, actions: resp?.actions, timeline: resp?.timeline };
    setChatMessages(prev => [...prev, userMsg, agentMsg]);
    setChatMode(true);
  };

  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };

  const pushMessage = (msg) => setChatMessages((prev) => [...prev, msg]);
  const addDeepDiveTimer = (cb, delay) => {
    const id = setTimeout(cb, scaleDelay(delay));
    deepDiveTimersRef.current.push(id);
  };
  const resetChat = () => {
    deepDiveTimersRef.current.forEach(clearTimeout);
    deepDiveTimersRef.current = [];
    setDeepDiveBranch(null);
    setDeepDiveTurn(0);
    setChatMessages([]);
  };
  const lockPreviousChips = () => {
    setChatMessages((prev) => prev.map((m) => (m.chips ? { ...m, chipsDisabled: true } : m)));
  };

  const appendConversationClosure = () => {
    addDeepDiveTimer(() => pushMessage({ role: 'user', text: 'Perfect. Thanks, Sally — keep me posted.' }), 2000);
    addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 2600);
    addDeepDiveTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, { role: 'agent', text: 'Will do. Talk soon.' }];
      });
    }, 3800);
    addDeepDiveTimer(() => pushMessage({ role: 'complete-pill' }), 4600);
  };

  // Top-right chip-driven Deep Dive
  const handleDeepDive = () => {
    resetChat();
    const opening = SALES_DEEP_DIVE.opening;
    setChatMessages([{ role: 'user', text: opening.ceo }]);
    setChatMode(true);
    addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 500);
    addDeepDiveTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, { role: 'agent', text: opening.sally, chips: opening.chips }];
      });
    }, 2000);
  };

  const handleChipClick = (chip) => {
    lockPreviousChips();
    pushMessage({ role: 'user', text: chip.label });
    if (!deepDiveBranch) {
      const branchKey = chip.id;
      const turns = SALES_DEEP_DIVE.branches[branchKey];
      if (!turns) return;
      setDeepDiveBranch(branchKey);
      setDeepDiveTurn(0);
      addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 500);
      addDeepDiveTimer(() => {
        const first = turns[0];
        setChatMessages((prev) => {
          const withoutThinking = prev.filter((m) => m.role !== 'thinking');
          return [...withoutThinking, normalizeTurn(first)];
        });
      }, 1800);
      return;
    }
    const turns = SALES_DEEP_DIVE.branches[deepDiveBranch];
    if (!turns) return;
    const nextIdx = deepDiveTurn + 1;
    setDeepDiveTurn(nextIdx);
    const nextTurn = turns[nextIdx];
    if (!nextTurn) return;
    const resolved = nextTurn.role === 'meta' && nextTurn.perChip ? nextTurn.perChip[chip.id] : nextTurn;
    if (!resolved) return;
    addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 400);
    addDeepDiveTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, normalizeTurn(resolved)];
      });
    }, 1600);
    if ((resolved.davidTask || resolved.closure) && !resolved.chips) {
      addDeepDiveTimer(() => appendConversationClosure(), 1800);
    }
  };

  // Auto-play flat scripts (decisions, alerts, deals)
  const autoPlayScript = (turns) => {
    const playNext = (idx) => {
      if (idx >= turns.length) return;
      const turn = turns[idx];
      if (turn.role === 'user') {
        addDeepDiveTimer(() => { pushMessage({ role: 'user', text: turn.text }); playNext(idx + 1); }, 1400);
        return;
      }
      if (turn.role === 'complete-pill') {
        addDeepDiveTimer(() => { pushMessage({ role: 'complete-pill' }); playNext(idx + 1); }, 700);
        return;
      }
      addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 500);
      const textPause = 2100 + (turn.text ? Math.min(turn.text.length * 3, 1200) : 0);
      addDeepDiveTimer(() => {
        setChatMessages((prev) => {
          const withoutThinking = prev.filter((m) => m.role !== 'thinking');
          return [...withoutThinking, {
            role: 'agent', text: turn.text,
            executionSteps: turn.executionSteps || null,
            davidTask: turn.davidTask || null,
            closure: turn.closure || null,
          }];
        });
        if (turn.ripple) {
          if (turn.ripple.approveDecision) {
            setApprovedDecisions((prev) => { const next = new Set(prev); next.add(turn.ripple.approveDecision); return next; });
          }
          if (turn.ripple.actionAlert) {
            setAddressedAlerts((prev) => { const next = new Set(prev); next.add(turn.ripple.actionAlert); return next; });
          }
        }
        playNext(idx + 1);
      }, textPause);
    };
    playNext(0);
  };

  useEffect(() => {
    return () => deepDiveTimersRef.current.forEach(clearTimeout);
  }, []);

  const handleAlertDive = (alert) => {
    resetChat();
    setChatMode(true);
    const script = alert?.id ? SALES_ALERT_DEEP_DIVES[alert.id] : null;
    if (script) { autoPlayScript(script); return; }
    // Deals use the same alert-dive entry from DealRow/DecisionCard stub
    const dealId = alert?.id && alert.id.startsWith('deal') ? alert.id : null;
    const dealScript = dealId ? SALES_DEAL_DEEP_DIVES[dealId] : null;
    if (dealScript) { autoPlayScript(dealScript); return; }
    // Fallback
    setChatMessages([{ role: 'user', text: `Tell me more about "${alert.title || 'this'}".` }]);
    addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 500);
    addDeepDiveTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, { role: 'agent', text: alert.description || 'No additional context available.' }];
      });
    }, 2100);
  };

  const handleDealDeepDive = (dealId) => {
    const script = SALES_DEAL_DEEP_DIVES[dealId];
    if (!script) return;
    resetChat();
    setChatMode(true);
    autoPlayScript(script);
  };

  const handleDecisionDeepDive = (decisionId) => {
    const script = SALES_DECISION_DEEP_DIVES[decisionId];
    if (!script) return;
    resetChat();
    setChatMode(true);
    autoPlayScript(script);
  };

  const handleEscalate = (alertId) => {
    setEscalatedAlerts(prev => new Set([...prev, alertId]));
    const alert = data.alerts.find(a => a.id === alertId);
    if (onToast && alert) onToast({ type: 'handoff', title: 'Escalated to David', message: `Sally notified David: "${alert.title}"` });
  };

  const handleApproveDecision = (decisionId) => {
    setApprovedDecisions(prev => new Set([...prev, decisionId]));
    const decision = data.decisions.find(d => d.id === decisionId);
    if (onToast && decision) onToast({ type: 'success', title: 'Approved · David notified', message: `${decision.title} — Sally is executing now.` });
  };

  const handleInput = (e) => {
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
    setMessage(e.target.value);
  };

  /* ── Chat input ── */
  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={handleInput} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Sally about Sales..." rows={1}
              className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`}
              style={{ height: 'auto', maxHeight: '200px', overflow: message.split('\n').length > 8 ? 'auto' : 'hidden' }}
              data-testid="sales-dept-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.sally} alt="" className="w-5 h-5 rounded-full object-cover" />
                <span className={`text-xs font-medium ${tp}`}>Sally</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="sales-dept-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Chat view ── */
  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="sales-dept-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="sales-dept-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Sales</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <style>{`@keyframes salesFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          <div className="flex justify-center">
            <div className="w-[80%] py-6">
              {chatMessages.map((msg, i) => (
                <div key={i} style={{ marginTop: i > 0 ? 32 : 0 }}>
                  <ChatMessage msg={msg} isDark={isDark} onChipClick={handleChipClick} chipsDisabled={msg.chipsDisabled} />
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>
        {chatInput}
      </div>
    );
  }

  /* ── Data view (default) ── */
  return (
    <div className="flex flex-col h-full" data-testid="sales-dept-page">
      {/* Health Header (above divider — matches Marketing) */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center gap-5">
          <ScoreRing score={data.health.score} size={68} strokeWidth={4} isDark={isDark} status={data.health.status} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <TrendingUp className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              <p className={`text-sm font-semibold ${tp}`}>Sales</p>
              <div className="flex items-center gap-1">
                <TrendingDown className={`w-3 h-3 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
                <span className={`text-[11px] ${isDark ? 'text-red-400' : 'text-red-500'}`}>{data.health.trend} pts</span>
              </div>
            </div>
            <p className={`text-[11px] mt-0.5 ${ts}`}>{data.health.summary}</p>
          </div>
          <button onClick={handleDeepDive}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`}
            data-testid="sales-deep-dive-btn">
            <Search className="w-3.5 h-3.5" /> Deep Dive
          </button>
        </div>
      </div>

      <div className={`border-t ${border}`} />

      {/* 3-column grid (matches Marketing layout) */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="grid grid-cols-3 gap-5 h-full px-5 py-4">

          {/* Column 1: Sales Agents & Alerts */}
          <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
            <div className="flex items-center gap-2 mb-3">
              <Bot className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Sales Agents</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-emerald-400/10 text-emerald-400' : 'bg-emerald-600/10 text-emerald-600'}`}>
                {data.agents.filter(a => a.status === 'active').length} active
              </span>
            </div>
            <div className="flex flex-col gap-2 mb-4">
              {data.agents.map(a => <AgentCard key={a.id} agent={a} isDark={isDark} />)}
            </div>

            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Alerts</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-red-400/10 text-red-400' : 'bg-red-600/10 text-red-600'}`}>{data.alerts.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {data.alerts.map(a => <AlertCard key={a.id} alert={a} isDark={isDark} onDeepDive={handleAlertDive} onEscalate={handleEscalate} escalated={escalatedAlerts.has(a.id)} />)}
            </div>
          </div>

          {/* Column 2: Active Deals & Needs Your Decision */}
          <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
            <div className="flex items-center gap-2 mb-3">
              <Target className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Active Deals</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>{data.deals.length}</span>
            </div>
            <div className="flex flex-col gap-2 mb-4">
              {data.deals.map(d => <DealRow key={d.id} deal={d} isDark={isDark} onDeepDive={handleDealDeepDive} />)}
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Shield className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Needs Your Decision</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>
                {data.decisions.filter(d => !approvedDecisions.has(d.id)).length}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {data.decisions.map(d => <DecisionCard key={d.id} item={d} isDark={isDark} onApprove={handleApproveDecision} onDeepDive={handleDecisionDeepDive} approved={approvedDecisions.has(d.id)} />)}
            </div>
          </div>

          {/* Column 3: Pipeline, Metrics, Agent Activity */}
          <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Pipeline by Stage</p>
            </div>
            <div className="mb-4">
              <PipelineFunnel stages={data.pipeline} isDark={isDark} />
            </div>

            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Key Metrics</p>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {data.metrics.map((m, i) => (
                <div key={i} className={`px-2.5 py-2 rounded-lg border ${m.alert ? (isDark ? 'border-red-500/15 bg-red-500/5' : 'border-red-200/50 bg-red-50/50') : `${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}`}>
                  <p className={`text-[9px] ${ts}`}>{m.label}</p>
                  <div className="flex items-baseline gap-1.5">
                    <span className={`text-base font-bold ${m.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{m.value}</span>
                    <span className={`text-[10px] font-medium ${m.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{m.trend}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Zap className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Agent Activity</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>Live</span>
            </div>
            <div className={`flex flex-col divide-y ${isDark ? 'divide-white/[0.04]' : 'divide-black/[0.04]'}`}>
              {data.recentActivity.map(item => <ActivityItem key={item.id} item={item} isDark={isDark} />)}
            </div>
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4SalesDept;
